import { useState } from "react";

import { compactCents, formatCivilDate, formatFreshness } from "../../utils/directorFormat";

// Painel único de títulos anulados na Ampli (SPEC 2026-10-20 §8). Usado pela Diretoria e pelo
// Backoffice: a lista é da empresa inteira e vale para a Diretoria e para o Ampli Obras.
// `api` entrega `{ ok, status, body }` (nunca lança): { preview, annul, revoke, reprocess }.

export const TITLE = "Títulos anulados na Ampli";
export const CONFIRMATION_TEXT = "Vale para a Diretoria e para o Ampli Obras. O Sienge não muda.";
export const QUOTA_BLOCKED_TEXT = "Anulação registrada. Entra no próximo processamento.";
export const PENDING_LABEL = "Ainda não aplicado";
export const NOT_FOUND_LABEL = "Não encontrado no último processamento";
export const DIRECTOR_NOTE = "Os números da Diretoria se atualizam no próximo ciclo ou pelo botão \"Atualizar\". Reprocessar aqui atualiza o Ampli Obras.";

export const CATEGORY_LABELS = {
  erp_forecast: "Previsão do ERP (PRV)",
  duplicate: "Duplicado",
  out_of_scope: "Fora do escopo da análise",
  other: "Outro motivo",
};

export const AUTOMATIC_LABELS = {
  erp_forecast_with_schedule: "Previsão do ERP em obra com cronograma: o cronograma vence",
  erp_forecast_receivable: "Previsão de receita do ERP",
};

export const ORIGIN_LABELS = { ampli_obras: "Ampli Obras", backoffice: "Backoffice", director: "Diretoria" };

const SIDE_LABELS = { outflow: "A pagar", inflow: "A receber" };
const REASON_MIN = 10;
const REASON_MAX = 500;

const civil = (value) => (typeof value === "string" ? formatCivilDate(value.slice(0, 10)) : "—");
const person = (value) => value?.name || "Usuário";
const brl = (value) => (Number.isFinite(value)
  ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
  : "—");
const countOf = (value) => (Array.isArray(value) ? value.length : Number(value) || 0);
const obras = (count) => (count === 1 ? "1 obra" : `${count} obras`);

const dueRange = (effect) => {
  if (!effect?.firstDueDate) return "—";
  return effect.firstDueDate === effect.lastDueDate
    ? civil(effect.firstDueDate)
    : `${civil(effect.firstDueDate)} a ${civil(effect.lastDueDate)}`;
};

// Efeito da Diretoria: centavos no ciclo da Diretoria (`effect.excludedCents`).
const directorEffectText = (item, lastCycleAt) => {
  if (item.state !== "active") return "Volta a entrar a partir do próximo ciclo";
  if (item.effect?.excludedCents !== undefined) {
    return `${compactCents(item.effect.excludedCents)} · ${item.effect.installmentCount} parcela(s) · ${dueRange(item.effect)}`;
  }
  if (!lastCycleAt || (item.createdAt && item.createdAt > lastCycleAt)) return "Sai da análise no próximo ciclo";
  return "Não encontrado no último ciclo";
};

// Efeito no Ampli Obras: `worksEffect` (Diretoria) ou `effect` do núcleo (Backoffice), em reais.
const worksEffectOf = (item) => item.worksEffect || (item.effect?.buildings ? item.effect : null);

const worksEffectText = (item) => {
  const works = worksEffectOf(item);
  if (!works?.buildings?.length) return null;
  const total = works.buildings.reduce((sum, building) => sum + (Number(building.removedToPay) || 0), 0);
  return `Ampli Obras: ${brl(total)} a pagar fora da conta em ${obras(works.buildings.length)}`;
};

const errorMessage = (result) => (
  result?.body?.message || (result?.status === 403
    ? "Você não tem permissão para alterar a lista."
    : "Não foi possível salvar. Tente de novo.")
);

const validReason = (value) => {
  const { length } = String(value || "").trim();
  return length >= REASON_MIN && length <= REASON_MAX;
};

const reasonHint = `Entre ${REASON_MIN} e ${REASON_MAX} caracteres.`;

const emptyForm = (legalEntities) => ({
  legalEntityId: legalEntities.length === 1 ? String(legalEntities[0].id) : "",
  billId: "",
  direction: "outflow",
  category: "erp_forecast",
  reason: "",
});

// A trava de atualização é por empresa: uma obra por vez. O resto fica em `alreadyRunning`.
const reprocessMessage = (body) => {
  if (body?.blocked) return { tone: "success", text: QUOTA_BLOCKED_TEXT };
  const queued = countOf(body?.queued);
  const remaining = countOf(body?.alreadyRunning);
  if (queued > 0) {
    return {
      tone: "success",
      text: remaining > 0
        ? `${obras(queued)} em atualização. Faltam ${remaining} — reprocesse de novo quando terminar.`
        : `${obras(queued)} em atualização.`,
    };
  }
  if (remaining > 0) {
    return {
      tone: "success",
      text: `Já há uma atualização em andamento nesta empresa. Faltam ${remaining} — reprocesse de novo quando terminar.`,
    };
  }
  return { tone: "success", text: "Nada a reprocessar agora." };
};

const PreviewSummary = ({ preview }) => <div className="title-annulments__preview" role="region" aria-label="Prévia do título">
  {!preview.found && <p role="alert">
    Título não encontrado nos dados da Ampli. Dá para anular mesmo assim, mas confira o número e a empresa.
  </p>}
  {preview.ambiguousEntity && <p role="alert">
    Este número existe em obras de empresas diferentes do Sienge. Confira a empresa antes de anular.
  </p>}
  {preview.alreadyAnnulled && <p role="alert">Este título já está anulado na Ampli.</p>}
  {preview.found && <dl className="title-annulments__preview-list">
    <div><dt>Documento</dt><dd>{preview.documentType || "—"}</dd></div>
    <div><dt>Credor</dt><dd>{preview.creditorName || "—"}</dd></div>
    <div><dt>A pagar</dt><dd>{brl(preview.toPay)}</dd></div>
    <div><dt>Pago</dt><dd>{brl(preview.paid)}</dd></div>
    <div><dt>Parcelas</dt><dd>{(preview.installments || []).length}</dd></div>
    <div><dt>Obras</dt><dd>{(preview.buildings || []).length ? preview.buildings.map((building) => building.buildingId).join(", ") : "—"}</dd></div>
  </dl>}
</div>;

const TitleAnnulmentsPanel = ({ data, reload, api, companyId, variant = "backoffice" }) => {
  const legalEntities = data?.legalEntities || [];
  const [form, setForm] = useState(() => emptyForm(legalEntities));
  const [preview, setPreview] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  if (!data) return null;
  const items = data.items || [];
  const automatic = data.automatic || [];
  const active = items.filter((item) => item.state === "active");
  const history = items.filter((item) => item.state !== "active");
  const field = (name) => (event) => {
    setPreview(null);
    setForm((current) => ({ ...current, [name]: event.target.value }));
  };
  // O portfólio chega depois do primeiro render: com uma única SPE ela já vale como escolha.
  const legalEntityId = form.legalEntityId || (legalEntities.length === 1 ? String(legalEntities[0].id) : "");
  const target = () => ({
    legalEntityId: Number(legalEntityId),
    billId: Number(form.billId),
    direction: form.direction,
  });
  const billValid = /^[0-9]{1,9}$/.test(form.billId.trim());

  const run = async (action, success) => {
    setBusy(true);
    setMessage(null);
    const result = await action();
    setBusy(false);
    if (result?.ok) {
      setMessage(typeof success === "function" ? success(result.body) : { tone: "success", text: success });
      reload();
      return true;
    }
    setMessage({ tone: "error", text: errorMessage(result) });
    return false;
  };

  const lookup = async (event) => {
    event.preventDefault();
    if (!billValid) return;
    setBusy(true);
    setMessage(null);
    const result = await api.preview(companyId, target());
    setBusy(false);
    if (result?.ok) setPreview(result.body || { found: false });
    else setMessage({ tone: "error", text: errorMessage(result) });
  };

  const annul = async () => {
    const billId = Number(form.billId);
    const ok = await run(() => api.annul(companyId, {
      ...target(),
      category: form.category,
      reason: form.reason.trim(),
    }), `Título ${billId} anulado. ${CONFIRMATION_TEXT}`);
    if (ok) {
      setForm(emptyForm(legalEntities));
      setPreview(null);
    }
  };

  const revoke = async (event) => {
    event.preventDefault();
    const { item, reason } = revoking;
    const ok = await run(
      () => api.revoke(companyId, item.id, { revokeReason: reason.trim() }),
      `Título ${item.billId} reincluído.`,
    );
    if (ok) setRevoking(null);
  };

  const reprocess = (item) => run(() => api.reprocess(companyId, { annulmentIds: [item.id] }), reprocessMessage);

  const formReady = legalEntityId && billValid;
  const confirmReady = preview && !preview.alreadyAnnulled && validReason(form.reason);
  const columns = data.canWrite ? 7 : 6;

  return <section className="title-annulments" aria-labelledby="title-annulments-title">
    <header className="director-module1__section-header">
      <h2 id="title-annulments-title">{TITLE}</h2>
      <p>
        Títulos do Sienge que saem do Ampli Obras e da Diretoria, sem mexer no Sienge. Cada anulação guarda o motivo,
        quem anulou e quanto deixou de somar.
        {data.lastCycleAt ? ` Último ciclo da Diretoria em ${formatFreshness(data.lastCycleAt)}.` : ""}
      </p>
      {variant === "director" && <p className="title-annulments__director-note">{DIRECTOR_NOTE}</p>}
    </header>
    {message && <p className="title-annulments__message" data-tone={message.tone} role="status">{message.text}</p>}
    {data.truncated && <p className="title-annulments__message" role="note">A lista está parcial: só os títulos mais recentes aparecem.</p>}
    <div className="director-table-scroll">
      <table className="director-table title-annulments__table" aria-label="Títulos anulados manualmente">
        <thead>
          <tr>
            <th scope="col">Título</th>
            <th scope="col">Empresa no Sienge</th>
            <th scope="col">Lado</th>
            <th scope="col">Motivo</th>
            <th scope="col">Anulado por</th>
            <th scope="col">Efeito</th>
            {data.canWrite && <th scope="col"><span className="visually-hidden">Ação</span></th>}
          </tr>
        </thead>
        <tbody>
          {active.length === 0 && <tr><td colSpan={columns}>Nenhum título anulado manualmente.</td></tr>}
          {active.map((item) => <tr key={item.id}>
            <th scope="row">{item.billId}</th>
            <td>{item.legalEntityName || item.legalEntityId}</td>
            <td>{SIDE_LABELS[item.direction] || item.direction}</td>
            <td><strong>{CATEGORY_LABELS[item.category] || item.category}</strong><br />{item.reason}</td>
            <td>{person(item.createdBy)}, {civil(item.createdAt)}{ORIGIN_LABELS[item.createdVia] && <><br />{ORIGIN_LABELS[item.createdVia]}</>}</td>
            <td>
              {variant === "director" && <p>{directorEffectText(item, data.lastCycleAt)}</p>}
              {worksEffectText(item) && <p>{worksEffectText(item)}</p>}
              {item.pendingApplication && <p><em>{PENDING_LABEL}</em></p>}
              {item.notFoundInLastRun && <p><em>{NOT_FOUND_LABEL}</em></p>}
            </td>
            {data.canWrite && <td>
              {item.pendingApplication && <button className="director-button director-button--secondary" type="button" disabled={busy} onClick={() => reprocess(item)}>
                Reprocessar agora
              </button>}
              <button className="director-button director-button--secondary" type="button" disabled={busy} onClick={() => setRevoking({ item, reason: "" })}>
                Reincluir
              </button>
            </td>}
          </tr>)}
        </tbody>
      </table>
    </div>
    {data.canWrite && revoking && <form className="title-annulments__revoke" onSubmit={revoke} aria-label="Reincluir título">
      <h3>Reincluir o título {revoking.item.billId}?</h3>
      <label className="director-filter-field">
        <span>Motivo da reinclusão</span>
        <textarea
          required
          minLength={REASON_MIN}
          maxLength={REASON_MAX}
          value={revoking.reason}
          onChange={(event) => setRevoking((current) => ({ ...current, reason: event.target.value }))}
        />
      </label>
      <small>{reasonHint}</small>
      <button className="director-button" type="submit" disabled={busy || !validReason(revoking.reason)}>Confirmar reinclusão</button>
      <button className="director-button director-button--secondary" type="button" disabled={busy} onClick={() => setRevoking(null)}>Cancelar</button>
    </form>}
    {automatic.length > 0 && <div className="director-table-scroll">
      <table className="director-table title-annulments__table" aria-label="Títulos retirados automaticamente">
        <thead>
          <tr>
            <th scope="col">Título</th>
            <th scope="col">Empresa no Sienge</th>
            <th scope="col">Lado</th>
            <th scope="col">Regra</th>
            <th scope="col">Valor fora da análise</th>
            <th scope="col">Vencimentos</th>
          </tr>
        </thead>
        <tbody>
          {automatic.map((item) => <tr key={`${item.origin}-${item.legalEntityId}-${item.billId}-${item.direction}`}>
            <th scope="row">{item.billId}</th>
            <td>{item.legalEntityName || item.legalEntityId}</td>
            <td>{SIDE_LABELS[item.direction] || item.direction}</td>
            <td>{AUTOMATIC_LABELS[item.origin] || item.origin}</td>
            <td>{compactCents(item.excludedCents)} · {item.installmentCount} parcela(s)</td>
            <td>{dueRange(item)}</td>
          </tr>)}
        </tbody>
      </table>
    </div>}
    {history.length > 0 && <details className="title-annulments__history">
      <summary>Reincluídos ({history.length})</summary>
      <ul>
        {history.map((item) => <li key={item.id}>
          Título {item.billId} ({SIDE_LABELS[item.direction] || item.direction}, {item.legalEntityName || item.legalEntityId}):
          anulado por {person(item.createdBy)} em {civil(item.createdAt)}, reincluído por {person(item.revokedBy)} em {civil(item.revokedAt)}.
          Motivo da anulação: {item.reason}.{item.revokeReason ? ` Motivo da reinclusão: ${item.revokeReason}.` : ""}
          {/* Reincluído ainda presente no último efeito: só volta ao Ampli Obras depois de reprocessar. */}
          {item.pendingApplication && <>
            {" "}<em>{PENDING_LABEL}</em>
            {data.canWrite && <>
              {" "}<button className="director-button director-button--secondary" type="button" disabled={busy} onClick={() => reprocess(item)}>
                Reprocessar agora
              </button>
            </>}
          </>}
        </li>)}
      </ul>
    </details>}
    {data.canWrite && <form className="title-annulments__form" onSubmit={lookup} aria-label="Anular um título na Ampli">
      <h3>Anular um título</h3>
      <label className="director-filter-field">
        <span>Empresa no Sienge</span>
        {legalEntities.length > 0
          ? <select required value={legalEntityId} onChange={field("legalEntityId")}>
            <option value="" disabled>Escolha</option>
            {legalEntities.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}
          </select>
          : <input required inputMode="numeric" pattern="[0-9]{1,9}" value={form.legalEntityId} onChange={field("legalEntityId")} />}
      </label>
      <label className="director-filter-field">
        <span>Número do título</span>
        <input required inputMode="numeric" pattern="[0-9]{1,9}" value={form.billId} onChange={field("billId")} />
      </label>
      <label className="director-filter-field">
        <span>Lado</span>
        <select value={form.direction} onChange={field("direction")}>
          <option value="outflow">A pagar</option>
          <option value="inflow">A receber</option>
        </select>
      </label>
      <label className="director-filter-field">
        <span>Categoria</span>
        <select value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label className="director-filter-field title-annulments__reason">
        <span>Motivo</span>
        <textarea
          required
          minLength={REASON_MIN}
          maxLength={REASON_MAX}
          value={form.reason}
          onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
        />
      </label>
      <small>{reasonHint}</small>
      <button className="director-button director-button--secondary" type="submit" disabled={busy || !formReady}>Ver título</button>
      {preview && <>
        <PreviewSummary preview={preview} />
        <p className="title-annulments__confirmation">{CONFIRMATION_TEXT}</p>
        <button className="director-button" type="button" disabled={busy || !confirmReady} onClick={annul}>Anular na Ampli</button>
      </>}
    </form>}
  </section>;
};

export default TitleAnnulmentsPanel;
